import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { colors, borderRadius, spacing } from '../styles/designTokens';
import type { DashboardKeywordListItem } from '../models/DashboardModels';
import { MobileDetailButton, MobileTableSection, MobileTableHeader } from '../styles/DashboardMobileStyles';
import type { DashboardKeywordTableVariant } from './DashboardKeywordTable';

interface DashboardKeywordTableMobileProps {
  items: DashboardKeywordListItem[];
  variant: DashboardKeywordTableVariant;
}

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
`;

const Card = styled.div`
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[3]};
  background: ${colors.semantic.background.primary};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  font-size: 12px;
  color: ${colors.semantic.text.secondary};
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Strong = styled.span`
  font-weight: 800;
  color: ${colors.semantic.text.primary};
`;

const UtilBadge = styled.span<{ $on: boolean }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${borderRadius.base};
  font-size: 11px;
  font-weight: 800;
  ${({ $on }) =>
    $on
      ? `
    background: ${colors.success[100]};
    color: ${colors.success[800]};
  `
      : `
    background: ${colors.error[50]};
    color: ${colors.error[800]};
  `}
`;

const titles: Record<DashboardKeywordTableVariant, string> = {
  active: '활성 키워드',
  inactive: '비활성 키워드'
};

const DashboardKeywordTableMobile: React.FC<DashboardKeywordTableMobileProps> = ({
  items,
  variant
}) => {
  const navigate = useNavigate();
  const showUtil = variant === 'active';

  return (
    <MobileTableSection>
      <MobileTableHeader>
        <h3>{titles[variant]}</h3>
      </MobileTableHeader>
      <CardList>
        {items.length === 0 && (
          <div style={{ textAlign: 'center', padding: 24, color: colors.semantic.text.tertiary }}>
            표시할 키워드가 없습니다.
          </div>
        )}
        {items.map(row => {
          const utilOn = row.잔여포인트 >= 0;
          return (
            <Card key={`${row.user_idx}-${row.keyword_idx}`}>
              <Row>
                <Strong>{row.키워드}</Strong>
                <span>· {row.브랜드명}</span>
              </Row>
              <Row>
                <span>{row.고객명}</span>
                <span>({row.아이디})</span>
              </Row>
              <Row>
                <span>{row.타겟사이트}</span>
                <span>{row.수집주기}</span>
                <span>{row.분석유형}</span>
              </Row>
              <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <span>
                  잔여 <Strong>{row.잔여포인트.toLocaleString('ko-KR')}</Strong> P
                </span>
                {showUtil ? (
                  <UtilBadge $on={utilOn}>활용 {utilOn ? 'ON' : 'OFF'}</UtilBadge>
                ) : null}
                <MobileDetailButton type="button" onClick={() => navigate(`/mobile/customers/${row.user_idx}`)}>
                  보기
                </MobileDetailButton>
              </Row>
            </Card>
          );
        })}
      </CardList>
    </MobileTableSection>
  );
};

export default DashboardKeywordTableMobile;
